import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { Url } from '@wix/da-url';
import {
  PureBiData,
  ClickDownloadButtonBiEvent,
  BuyPremiumDownloadClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import { Props as BuyButtonProps } from '@wix/da-ds/pkg/Buttons/BuyButton/BuyButton';
import { useContentFilter } from '../../../utils/hooks/useContentFilter';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import { LoggedOutReturnType } from '../../../redux/loggedout';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import { formatDollars } from '../../../utils/formatting';
import PriceWithDiscount from '../../../PriceWithDiscount';
import { getDeviationSaleDetails } from '../../../utils/deviations';
import DownloadButtonProps from './types';
import { getDeviationPremiumDownload } from './permissions';
import SisuActionPopover from '../../_partials/SisuActionPopover';

import s from './PremiumDownloadButton.scss';

const logger = ddt.createLogger('downloadbutton');

const PremiumDownloadButton: React.FC<React.PropsWithChildren<DownloadButtonProps>> = ({
  deviation,
  className,
  onDownload,
}) => {
  // note: mouseEnter and mouseExit aren't used since this button isn't an icon button,
  // doesn't need a tooltip itself, and, bc of that, doesn't need to pause the comment tooltip
  const { t } = useTranslation();
  const isSisuPopoverEnabled = useFlag('sisu_popover');
  const { isLoggedIn, sendUserToJoinPage } = useRestrictToLoggedIn();
  const { contentFilter, pushUserSettingsModal } = useContentFilter();
  const { eligibleDiscount, hasDiscounts } = useMemo(
    () => getDeviationSaleDetails(deviation),
    [deviation]
  );

  // when the user first purchases a subscription, open the tooltip and highlight the icon
  const isButtonHighlighted = useContext(SubscriptionPurchaseFTUEContext) === 'tooltips';

  logger.log('PremiumDownloadButton, deviation:', deviation);
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const { download, permission } = getDeviationPremiumDownload(deviation, isOwner);
  logger.log({ isOwner, download, permission });

  if (!download) {
    logger.log('has no downloads');
    return null;
  }

  const wrapperProps = {
    className: className,
    popoverWrapperClassName: s['wrapper'],
  };

  const isPurchasable = permission === 'purchasable';

  let buttonProps: BuyButtonProps = {
    biData: isPurchasable
      ? PureBiData<BuyPremiumDownloadClickBiEvent>({
          evid: 349,
          sectionname: 'action_bar',
          itemprice: download.downloadDollarPrice.toFixed(2),
          itemid: deviation.deviationId,
          typeid: deviation.typeId,
        })
      : PureBiData<ClickDownloadButtonBiEvent>({
          evid: 350,
          ispaiddownload: 1,
          deviationid: deviation.deviationId,
          itemid: deviation.deviationId,
          typeid: deviation.typeId,
          sectionname: 'action_bar',
        }),
    variant: isButtonHighlighted ? 'highlighted' : 'solid',
    size: 'medium',
    rounded: true,
    className: s['button'],
  };

  let buttonLabel = t('actionbar.download');
  if (isPurchasable) {
    const dollars = download.downloadDollarPrice;
    const cents = dollars - (0 | dollars);
    const price = formatDollars(download.downloadDollarPrice, {
      minimumFractionDigits: cents ? 2 : 0,
    });
    buttonLabel = price
      ? t('actionbar.downloadFor', { price })
      : t('actionbar.download.mustPurchase');
  }

  const checkoutUrl = deviation.extended?.pcp?.subproductId
    ? Url.checkoutLink(deviation.extended?.pcp?.subproductId)
    : undefined;

  // send anon to sisu
  if (!isLoggedIn) {
    const button = (
      <BuyButton
        {...buttonProps}
        onClick={
          isSisuPopoverEnabled
            ? undefined
            : () =>
                sendUserToJoinPage(
                  ReferralType.ANON_DOWNLOAD,
                  deviation.url,
                  LoggedOutReturnType.JOIN_ONLY
                )
        }
      >
        {buttonLabel}
      </BuyButton>
    );

    return (
      <ActionBarItemWrapper
        {...wrapperProps}
        tooltip={isSisuPopoverEnabled ? undefined : t('actionbar.download.mustLogIn')}
      >
        {isSisuPopoverEnabled ? (
          <SisuActionPopover
            restrictedActionName={ReferralType.ANON_DOWNLOAD}
            returnTo={deviation.url}
            returnType={LoggedOutReturnType.JOIN_ONLY}
          >
            {button}
          </SisuActionPopover>
        ) : (
          button
        )}
      </ActionBarItemWrapper>
    );
  }

  // still needs to purchase at checkout
  if (isPurchasable && !!checkoutUrl) {
    // we may need them to update their mature content settings before
    // sending them to checkout or ponies will 404 the page
    const needsBrowsingModeChange = deviation.isMature && contentFilter !== 'mature';
    buttonProps = needsBrowsingModeChange
      ? { ...buttonProps, onClick: () => pushUserSettingsModal() }
      : { ...buttonProps, href: checkoutUrl };

    // is it already purchased?
  } else if (permission === 'downloadable') {
    buttonProps = {
      ...buttonProps,
      href: download.url,
      target: '_blank',
      download: true,
      onClick: onDownload,
    };
  }

  const showDiscount = isPurchasable && hasDiscounts;
  return (
    <ActionBarItemWrapper {...wrapperProps} allowMobilePopover={isPurchasable}>
      <BuyButton {...buttonProps}>
        {!showDiscount && buttonLabel}
        {showDiscount && (
          <PriceWithDiscount
            dollarAmount={download.downloadDollarPrice}
            eligibleDiscount={eligibleDiscount}
            size={buttonProps.size === 'small' ? 'small' : 'medium'}
            prefix={t('sidebar.exclusives.buttons.buyer.buyFor')}
            reversedTheme={true}
            opaque={false}
          />
        )}
      </BuyButton>
    </ActionBarItemWrapper>
  );
};

PremiumDownloadButton.displayName = 'PremiumDownloadButton';
export default PremiumDownloadButton;
